/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { 
  X, 
  ExternalLink, 
  GitBranch, 
  Server, 
  Clock, 
  DollarSign,
  Activity,
  Terminal,
  Database,
  Key,
  Settings,
  Cpu,
  HardDrive,
  Wifi,
  Play,
  Square,
  RotateCcw,
  RefreshCw,
  Trash2,
  AlertTriangle,
  Loader
} from 'lucide-react';
import type { Deployment, DeploymentEvent, DeploymentMetrics } from '../../lib/api';
import { 
  getDeploymentStatusColor, 
  getDeploymentStatusLabel, 
  getEnvironmentTypeLabel,
  formatCost,
  formatUptime 
} from '../../lib/api/deployments';

interface DeploymentDetailsProps {
  deployment: Deployment;
  events: DeploymentEvent[];
  metrics?: DeploymentMetrics[];
  isOperating: boolean;
  operationInProgress?: string;
  onClose: () => void;
  onStart: () => void;
  onStop: () => void;
  onRestart: () => void;
  onUpdate: () => void;
  onDelete: () => void;
  onViewLogs: () => void;
  onRunMigrations: () => void;
  onCreateBackup: () => void;
}

type Tab = 'overview' | 'metrics' | 'logs' | 'database' | 'settings';

export function DeploymentDetails({
  deployment,
  events,
  metrics,
  isOperating,
  operationInProgress,
  onClose,
  onStart,
  onStop,
  onRestart,
  onUpdate,
  onDelete,
  onViewLogs,
  onRunMigrations,
  onCreateBackup,
}: DeploymentDetailsProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const statusColor = getDeploymentStatusColor(deployment.status);
  const statusLabel = getDeploymentStatusLabel(deployment.status);
  const environmentLabel = getEnvironmentTypeLabel(deployment.environmentType);

  const getStatusIndicatorColor = () => {
    switch (statusColor) {
      case 'green': return '#22c55e';
      case 'yellow': return '#eab308';
      case 'red': return '#ef4444';
      default: return '#9ca3af';
    }
  };

  const isRunning = deployment.status === 'running';
  const isStopped = deployment.status === 'stopped';
  const canStart = isStopped || deployment.status === 'failed';
  const canStop = isRunning || deployment.status === 'unhealthy';
  const canRestart = isRunning || deployment.status === 'unhealthy';
  const canUpdate = isRunning || isStopped;

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Activity size={16} /> },
    { id: 'metrics', label: 'Metrics', icon: <Cpu size={16} /> },
    { id: 'logs', label: 'Logs', icon: <Terminal size={16} /> },
    { id: 'database', label: 'Database', icon: <Database size={16} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={16} /> },
  ];

  const renderActionButton = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    disabled: boolean,
    variant: 'primary' | 'secondary' | 'danger' = 'secondary'
  ) => {
    const bgColors = {
      primary: '#0066cc',
      secondary: 'white',
      danger: '#ef4444',
    };
    const textColors = {
      primary: '#ffffff',
      secondary: '#374151',
      danger: '#ffffff',
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled || isOperating}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          fontSize: '14px',
          backgroundColor: disabled ? '#e5e7eb' : bgColors[variant],
          color: disabled ? '#9ca3af' : textColors[variant],
          border: variant === 'secondary' ? '1px solid #e5e7eb' : 'none',
          borderRadius: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
        }}
      >
        {isOperating && operationInProgress === label.toLowerCase() ? (
          <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          icon
        )}
        {label}
      </button>
    );
  };

  const renderOverviewTab = () => (
    <div style={{ display: 'grid', gap: '24px' }}>
      {/* Status & Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: getStatusIndicatorColor(),
              animation: statusColor === 'yellow' ? 'pulse 2s infinite' : undefined,
            }}
          />
          <div>
            <div style={{ fontWeight: 500, color: '#111827' }}>{statusLabel}</div>
            {deployment.statusMessage && (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{deployment.statusMessage}</div>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {canStart && renderActionButton('Start', <Play size={16} />, onStart, false, 'primary')}
          {canStop && renderActionButton('Stop', <Square size={16} />, onStop, false)}
          {canRestart && renderActionButton('Restart', <RotateCcw size={16} />, onRestart, false)}
          {canUpdate && renderActionButton('Update', <RefreshCw size={16} />, onUpdate, false, 'primary')}
        </div>
      </div>

      {/* Info Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
        <InfoCard
          icon={<ExternalLink size={18} />}
          label="URL"
          value={deployment.domain}
          isLink
        />
        <InfoCard
          icon={<Server size={18} />}
          label="Machine Type"
          value={`${deployment.machineType} • ${deployment.diskSizeGb}GB`}
        />
        <InfoCard
          icon={<GitBranch size={18} />}
          label="Git Branch"
          value={deployment.gitBranch || 'Not configured'}
        />
        <InfoCard
          icon={<Clock size={18} />}
          label="Uptime"
          value={formatUptime(deployment.uptime)}
        />
        <InfoCard
          icon={<DollarSign size={18} />}
          label="Current Cost"
          value={formatCost(deployment.currentCost)}
        />
        <InfoCard
          icon={<DollarSign size={18} />}
          label="Monthly Projection"
          value={`${formatCost(deployment.monthlyCostProjection)}/mo`}
        />
      </div>

      {/* Git Info */}
      {deployment.gitCommitSha && (
        <div style={{ 
          padding: '16px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
            Current Commit
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#111827' }}>
            {deployment.gitCommitSha.substring(0, 8)}
          </div>
          {deployment.deployedAt && (
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
              Deployed {new Date(deployment.deployedAt).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Recent Events */}
      {events.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
            Recent Activity
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {events.slice(0, 5).map((event) => (
              <div
                key={event.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: event.status === 'completed' ? '#22c55e' : 
                                    event.status === 'failed' ? '#ef4444' : '#eab308',
                  }} />
                  <span style={{ color: '#111827', textTransform: 'capitalize' }}>
                    {event.operation}
                  </span>
                  <span style={{ color: '#9ca3af' }}>
                    {event.status}
                  </span>
                </div>
                <span style={{ color: '#9ca3af' }}>
                  {new Date(event.startedAt).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderMetricsTab = () => (
    <div style={{ display: 'grid', gap: '24px' }}>
      {/* Resource Usage */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        <MetricCard
          icon={<Cpu size={20} />}
          label="CPU Usage"
          value={deployment.cpuUsage !== undefined ? `${deployment.cpuUsage.toFixed(1)}%` : '--'}
          color="#3b82f6"
        />
        <MetricCard
          icon={<Activity size={20} />}
          label="Memory Usage"
          value={deployment.memoryUsage !== undefined ? `${deployment.memoryUsage.toFixed(1)}%` : '--'}
          color="#8b5cf6"
        />
        <MetricCard
          icon={<HardDrive size={20} />}
          label="Disk Usage"
          value={deployment.diskUsage !== undefined ? `${deployment.diskUsage.toFixed(1)}%` : '--'}
          color="#22c55e"
        />
      </div>
      
      {/* Placeholder for charts */}
      <div style={{ 
        padding: '40px', 
        backgroundColor: '#f9fafb', 
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center',
        color: '#9ca3af'
      }}>
        <Activity size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
        <p>Detailed metrics charts coming soon</p>
        <p style={{ fontSize: '13px' }}>Real-time CPU, memory, and network graphs</p>
      </div>
    </div>
  );

  const renderLogsTab = () => (
    <div style={{ display: 'grid', gap: '16px' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        {['app', 'nginx', 'postgres'].map((source) => (
          <button
            key={source}
            style={{
              padding: '8px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '13px',
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {source}
          </button>
        ))}
      </div>
      
      <div style={{ 
        padding: '40px', 
        backgroundColor: '#1f2937', 
        borderRadius: '12px',
        minHeight: '300px',
        textAlign: 'center',
        color: '#9ca3af'
      }}>
        <Terminal size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
        <p style={{ color: '#d1d5db' }}>Log streaming coming soon</p>
        <button
          onClick={onViewLogs}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: '#374151',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          View External Logs
        </button>
      </div>
    </div>
  );

  const renderDatabaseTab = () => (
    <div style={{ display: 'grid', gap: '24px' }}>
      <div style={{ display: 'flex', gap: '12px' }}>
        {renderActionButton('Run Migrations', <Database size={16} />, onRunMigrations, !isRunning, 'primary')}
        {renderActionButton('Create Backup', <HardDrive size={16} />, onCreateBackup, !isRunning)}
      </div>
      
      <div style={{ 
        padding: '40px', 
        backgroundColor: '#f9fafb', 
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center',
        color: '#9ca3af'
      }}>
        <Database size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
        <p>Database management coming soon</p>
        <p style={{ fontSize: '13px' }}>Backups, restore, and psql access</p>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div style={{ display: 'grid', gap: '24px' }}>
      {/* Secrets */}
      <div>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Key size={16} />
          Secrets
        </h4>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          color: '#9ca3af',
          fontSize: '14px'
        }}>
          Secrets management coming soon
        </div>
      </div>

      {/* Danger Zone */}
      <div style={{ 
        padding: '20px',
        backgroundColor: '#fef2f2',
        borderRadius: '12px',
        border: '1px solid #fecaca'
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={16} />
          Danger Zone
        </h4>
        
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <Trash2 size={16} />
            Delete Deployment
          </button>
        ) : (
          <div>
            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#991b1b' }}>
              This will permanently delete the deployment and all associated resources.
              Type <strong>{deployment.environmentName}</strong> to confirm.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deployment.environmentName}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid #fca5a5',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
              <button
                onClick={() => {
                  if (deleteConfirmText === deployment.environmentName) {
                    onDelete();
                  }
                }}
                disabled={deleteConfirmText !== deployment.environmentName}
                style={{
                  padding: '10px 16px',
                  backgroundColor: deleteConfirmText === deployment.environmentName ? '#ef4444' : '#e5e7eb',
                  color: deleteConfirmText === deployment.environmentName ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: deleteConfirmText === deployment.environmentName ? 'pointer' : 'not-allowed',
                }}
              >
                Confirm Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '640px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
          animation: 'slideIn 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 600, color: '#111827' }}>
              {deployment.environmentName}
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
              {environmentLabel}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              borderRadius: '8px',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #e5e7eb',
          padding: '0 24px',
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #0066cc' : '2px solid transparent',
                color: activeTab === tab.id ? '#0066cc' : '#6b7280',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? 500 : 400,
                cursor: 'pointer',
                marginBottom: '-1px',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'metrics' && renderMetricsTab()}
          {activeTab === 'logs' && renderLogsTab()}
          {activeTab === 'database' && renderDatabaseTab()}
          {activeTab === 'settings' && renderSettingsTab()}
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function InfoCard({ icon, label, value, isLink }: { icon: React.ReactNode; label: string; value: string; isLink?: boolean }) {
  const content = (
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#f9fafb', 
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', color: '#6b7280' }}>
        {icon}
        <span style={{ fontSize: '13px' }}>{label}</span>
      </div>
      <div style={{ 
        fontSize: '14px', 
        fontWeight: 500, 
        color: isLink ? '#0066cc' : '#111827',
        wordBreak: 'break-all'
      }}>
        {value}
      </div>
    </div>
  );

  if (isLink && value) {
    return (
      <a 
        href={`https://${value}`} 
        target="_blank" 
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        {content}
      </a>
    );
  }

  return content;
}

function MetricCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f9fafb', 
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      textAlign: 'center'
    }}>
      <div style={{ color, marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
        {value}
      </div>
      <div style={{ fontSize: '13px', color: '#6b7280' }}>{label}</div>
    </div>
  );
}

